import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { DAMNodeType, NodeType } from '@/types/tree';
import { type Asset } from '@/types/asset';

type NodeTarget = {
  mode: 'node';
  /** Single-element tuple — the folder or album nodeId to pass server-side for resolution. */
  nodeIds: [string];
  sourceType: DAMNodeType;
  name: string;
  assets: undefined;
};

type AssetsTarget = {
  mode: 'assets';
  nodeIds: Array<string>;
  sourceType: NodeType.Assets | DAMNodeType.Asset;
  name: string | undefined;
  assets: Array<Asset>;
};

export type OperationTarget = NodeTarget | AssetsTarget;

/**
 * Single entry point for *reading* gallery selection state and resolving operation targets.
 *
 * Composes useGallerySelection, useBrowserStore (isAllSelected) and useTreeStore
 * (selectedNode) so consumers don't need to reach into each store individually. This is a
 * read/derive view and deliberately exposes no item setter — selection writes go through
 * useGallerySelection directly. Keeping the selection consistent with the assets on screen
 * is a separate concern, handled by useReconcileSelectionWithPage.
 *
 * `operationTarget` resolves the correct IDs for bulk operations:
 * - mode 'node'   → all assets in the node are effectively selected, either via
 *                   the "select all" flag or by individually selecting every asset
 *                   (detected when selectedAssets.length === rowCount). The
 *                   folder/album nodeId is passed to the backend for server-side
 *                   resolution — identical to the breadcrumb/sidebar download path.
 * - mode 'assets' → explicit partial selection; use selectedAssets directly.
 *
 * `nodeAssetsCount` (server pagination total) is read from useBrowserStore,
 * where gallery-layout writes it whenever the assets query resolves.
 *
 * `deselectAll` clears selectedAssets, resets the shift-click anchor, and
 * turns off isAllSelected — all in one call.
 */
export const useSelection = () => {
  const { selection, selectedAssets, deselectAllItems } = useGallerySelection();
  const { isAllSelected, setIsAllSelected, nodeAssetsCount } = useBrowserStore(
    useShallow((s) => ({
      isAllSelected: s.isAllSelected,
      setIsAllSelected: s.setIsAllSelected,
      nodeAssetsCount: s.nodeAssetsCount,
    })),
  );
  const selectedNode = useTreeStore((s) => s.selectedNode);

  const deselectAll = useCallback(() => {
    deselectAllItems();
    setIsAllSelected(false);
  }, [deselectAllItems, setIsAllSelected]);

  const allAssetsInNodeSelected =
    isAllSelected ||
    (nodeAssetsCount !== undefined && selectedAssets.length > 0 && selectedAssets.length === nodeAssetsCount);

  const selectedChild = selection.kind === 'nodes' ? selection.item : undefined;

  /**
   * The folder/album the details panel is describing: the selected child when the gallery holds
   * one, otherwise the active tree node.
   *
   * Both the panel's read queries and its name/description writes must resolve through this. When
   * they resolved it separately, the read followed the selected child while the write still used
   * the active node, so editing a selected child renamed the folder being browsed instead — and
   * for a selected album it PATCHed the albums endpoint with a folder id and came back
   * "Resource not found". One resolver is what stops those drifting apart again.
   */
  const describedNodeId = selectedChild?.id ?? selectedNode?.node?.id;
  /** The type of `describedNodeId`, resolved through the same fallback so the two never drift. */
  const describedNodeType = selectedChild?.type ?? selectedNode?.type;

  /*
   * Three cases, in precedence order, flattened out of a nested ternary — the third arm was reached
   * only by reading past two conditions to find it.
   *
   * A selected child folder/album resolves to a target describing that child. That is distinct from
   * "every asset in the folder I'm browsing is selected", which still resolves to the active node.
   */
  const operationTarget: OperationTarget = ((): OperationTarget => {
    if (selectedChild !== undefined) {
      return {
        mode: 'node',
        nodeIds: [selectedChild.id],
        sourceType: selectedChild.type,
        name: selectedChild.name,
        assets: undefined,
      };
    }

    if (allAssetsInNodeSelected && selectedNode !== undefined) {
      return {
        mode: 'node',
        nodeIds: [selectedNode.node.id],
        sourceType: selectedNode.type,
        name: selectedNode.node.name,
        assets: undefined,
      };
    }

    return {
      mode: 'assets',
      nodeIds: selectedAssets.map((a) => a.nodeId ?? a.id),
      sourceType: selectedAssets.length > 1 ? NodeType.Assets : DAMNodeType.Asset,
      name: selectedAssets.length === 1 ? selectedAssets[0].name : undefined,
      assets: selectedAssets,
    };
  })();

  return {
    selection,
    selectedAssets,
    /** The selected child folder/album, or undefined when the selection is assets. */
    selectedChild,
    describedNodeId,
    describedNodeType,
    selectedNode,
    isAllSelected,
    operationTarget,
    setIsAllSelected,
    deselectAll,
  };
};
